import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material'
import React, {
  type ChangeEvent,
  useCallback,
  useEffect,
  useState
} from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { MonitorUrlFieldInput } from 'components/status-checks/form/status-check/common/MonitorUrlFieldInput'
import { StatusCheckNameField } from 'components/status-checks/form/status-check/common/StatusCheckNameField'
import { GrafanaMonitorSearch } from 'components/status-checks/form/status-check/grafana/GrafanaMonitorSearch'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { GRAFANA_MONITOR_URL_PREFIX } from 'utils/status-checks/status-check-integrations-util-grafana'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

export enum GrafanaSelectionSource {
  SEARCH = 'search',
  ALERT_URL = 'alert_url'
}

const fieldsToWatch = [
  'externalIntegration',
  'grafanaSelector.type',
  'monitorUrl'
] as const

export const GrafanaMonitorSelection: React.FC = () => {
  const { serviceName } = useStatusCheckDetailsContext()
  const { control, setValue, trigger } = useFormContext<StatusCheckFormValues>()
  const { setIsStatusCheckConnectionValidated } = useStatusCheckDetailsContext()
  const [selectedAlertId, setSelectedAlertId] = useState<string>()

  const [externalIntegration, grafanaSelectorType, monitorUrl] = useWatch({
    control,
    name: fieldsToWatch
  })

  const handleRadioOptionChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, value: GrafanaSelectionSource) => {
      setValue('grafanaSelector.type', value, {
        shouldDirty: true
      })
    },
    [setValue]
  )

  useEffect(() => {
    setIsStatusCheckConnectionValidated(false)
  }, [grafanaSelectorType, monitorUrl, setIsStatusCheckConnectionValidated])

  return (
    <Stack spacing={4}>
      <StatusCheckNameField />
      <Stack spacing={2}>
        <Typography>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <Controller
          control={control}
          name="grafanaSelector.type"
          render={({ field }) => {
            return (
              <RadioGroup
                {...field}
                value={grafanaSelectorType}
                onChange={handleRadioOptionChange}
              >
                <FormControl sx={{ mb: 2 }}>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={<Radio value={GrafanaSelectionSource.SEARCH} />}
                      label="Search for Grafana Alert by name or tags"
                    />
                    <Box sx={{ pl: 4 }}>
                      <GrafanaMonitorSearch
                        disabled={
                          grafanaSelectorType !== GrafanaSelectionSource.SEARCH
                        }
                        initialSearchValue={serviceName}
                        selectedValue={selectedAlertId}
                        onSelect={async (alertId) => {
                          if (!externalIntegration) return

                          setSelectedAlertId(alertId)
                          setValue(
                            'monitorUrl',
                            `${externalIntegration.url}${GRAFANA_MONITOR_URL_PREFIX}${alertId}`,
                            { shouldDirty: true }
                          )
                          await trigger('monitorUrl')
                        }}
                      />
                    </Box>
                  </Stack>
                </FormControl>
                <FormControl>
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Radio value={GrafanaSelectionSource.ALERT_URL} />
                      }
                      label="Provide Grafana Alert URL"
                    />
                    <Controller
                      control={control}
                      name="monitorUrl"
                      render={({ field, fieldState }) => (
                        <Box sx={{ pl: 4, width: '100%' }}>
                          <MonitorUrlFieldInput
                            disabled={
                              grafanaSelectorType !==
                              GrafanaSelectionSource.ALERT_URL
                            }
                            externalIntegration={externalIntegration}
                            field={field}
                            fieldState={fieldState}
                            label="Grafana Alert URL"
                          />
                        </Box>
                      )}
                    />
                  </Stack>
                </FormControl>
              </RadioGroup>
            )
          }}
        />
      </Stack>
    </Stack>
  )
}
